import React from "react";
import { Box, Typography, Button } from "@mui/material";
import Modal from "@/components/Modal";

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  mainText: string;
  descriptionText: string;
  onConfirm: () => void;
  confirmButtonText?: string;
  isLoading?: boolean;
  loadingText?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  mainText,
  descriptionText,
  onConfirm,
  confirmButtonText = "Confirm",
  isLoading = false,
  loadingText = "Processing...",
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      boxSx={{
        maxWidth: 600,
        width: "100%",
        background: "#fff",
        borderRadius: "16px",
        boxShadow: "0 4px 32px rgba(0,0,0,0.10)",
        padding: "32px",
      }}
    >
      <Box>
        <Box sx={{ textAlign: "center", mb: 4, px: 3, py: 2 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, fontSize: "24px", fontWeight: 600 }}
          >
            {mainText}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "16px" }}
          >
            {descriptionText}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              flex: 1,
              height: "48px",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isLoading}
            sx={{
              flex: 1,
              height: "48px",
              fontSize: "16px",
              fontWeight: 600,
              backgroundColor: "#0076CE",
            }}
          >
            {isLoading ? loadingText : confirmButtonText}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

interface ConfirmationModalsProps {
  showSignatureConfirmation: boolean;
  setShowSignatureConfirmation: (show: boolean) => void;
  showPassSizingConfirmation: boolean;
  setShowPassSizingConfirmation: (show: boolean) => void;
  showRejectSizingConfirmation: boolean;
  setShowRejectSizingConfirmation: (show: boolean) => void;
  projectId?: number;
  onConfirmSignature: () => void;
  onConfirmPassSizing: () => void;
  onConfirmRejectSizing: () => void;
  isPassingSizingTool: boolean;
  isRejectingSizingTool: boolean;
}

const ConfirmationModals: React.FC<ConfirmationModalsProps> = ({
  showSignatureConfirmation,
  setShowSignatureConfirmation,
  showPassSizingConfirmation,
  setShowPassSizingConfirmation,
  showRejectSizingConfirmation,
  setShowRejectSizingConfirmation,
  projectId,
  onConfirmSignature,
  onConfirmPassSizing,
  onConfirmRejectSizing,
  isPassingSizingTool,
  isRejectingSizingTool,
}) => {
  return (
    <>
      <ConfirmationModal
        open={showSignatureConfirmation}
        onClose={() => setShowSignatureConfirmation(false)}
        mainText={`Are you sure you want to send project #${projectId} for signature?`}
        descriptionText="This action will initiate the signature process for this project."
        onConfirm={onConfirmSignature}
      />

      <ConfirmationModal
        open={showPassSizingConfirmation}
        onClose={() => setShowPassSizingConfirmation(false)}
        mainText={`Are you sure you want to pass project #${projectId}?`}
        descriptionText="This action will mark the sizing tool as passed for this project."
        onConfirm={onConfirmPassSizing}
        isLoading={isPassingSizingTool}
        loadingText="Processing..."
      />

      <ConfirmationModal
        open={showRejectSizingConfirmation}
        onClose={() => setShowRejectSizingConfirmation(false)}
        mainText={`Are you sure you want to reject project #${projectId}?`}
        descriptionText="This action will mark the sizing tool as failed for this project."
        onConfirm={onConfirmRejectSizing}
        confirmButtonText="Reject"
        isLoading={isRejectingSizingTool}
        loadingText="Processing..."
      />
    </>
  );
};

export default ConfirmationModals;
